import React, { useState, useEffect, useContext, useCallback } from "react";
import { SimpleForm, Button } from "simple-ui-components";

import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";

import { getTranslation } from "../languageProvider";
import { get } from "../../server-requests/serverRequests";
import { AuthContext } from "../authContextProvider";
import { LoadingBox } from "../shared/loading";
import { Log, ReplaceWord } from "../shared/utility";
import { FilterByStyled } from "../../theme";

export const FilterEquipment = ({ Filter }) => {
  const { InputGroup } = SimpleForm;

  const { snackbarHandler } = useContext(AuthContext);

  const [toFilter, setToFilter] = useState({});

  // brand
  const [brandList, setBrandList] = useState([]);
  const [loadingBrand, setLoadingBrand] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState([]);

  // model
  const [reloadModel, setReloadModel] = useState(false);
  const [modelList, setModelList] = useState([]);
  const [loadingModel, setLoadingModel] = useState(true);
  const [selectedModel, setSelectedModel] = useState([]);

  // client
  const [clientList, setClientList] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);
  const [selectedClient, setSelectedClient] = useState([]);

  // states
  const [loadingState, setLoadingState] = useState(true);
  const [stateList, setStateList] = useState([]);
  const [selectedState, setSelectedState] = useState([]);

  // get State List
  useEffect(() => {
    let isMounted = true;
    get("equipments/states/list")
      .then((state) => {
        if (isMounted) {
          let formattedState = [];
          if (state.length > 0)
            formattedState = state.map((item) => ({
              label: item.name,
              ...item,
              color: `#${item.color}`,
            }));
          setStateList(formattedState);
          setLoadingState(false);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get equipments/states/list" + status);
        setLoadingState(false);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get clients list
  useEffect(() => {
    let isMounted = true;
    get("clients/list")
      .then((client) => {
        if (isMounted) {
          let formattedClient = [];
          if (client.length > 0)
            formattedClient = client.map((item) => ({
              label: item.name,
              companyName: item.company.name,
              ...item,
            }));
          setClientList(formattedClient);
          setLoadingClient(false);
        }
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        setLoadingClient(false);
        Log("Error: get clients/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  // get Models List
  useEffect(() => {
    let isMounted = true;
    if (reloadModel && selectedBrand.length > 0) {
      get("models/list", {
        brandId: selectedBrand[0].id,
      })
        .then((model) => {
          if (isMounted) {
            let formattedModel = [];
            if (model.length > 0)
              formattedModel = model.map((item) => ({
                label: item.name,
                ...item,
              }));
            setModelList(formattedModel);
            setReloadModel(false);
            setLoadingModel(false);
          }
        })
        .catch((status) => {
          setReloadModel(false);
          setLoadingModel(false);
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: get models/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadModel, selectedBrand, snackbarHandler]);

  // get Brand List
  useEffect(() => {
    let isMounted = true;
    get("brands/list")
      .then((brand) => {
        if (isMounted) {
          let formattedBrand = [];
          if (brand.length > 0)
            formattedBrand = brand.map((item) => ({
              label: item.name,
              companyName: item.company.name,
              ...item,
            }));
          setBrandList(formattedBrand);
          setLoadingBrand(false);
        }
      })
      .catch((status) => {
        setLoadingBrand(false);
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: get brand/list" + status);
      });
    return () => (isMounted = false);
  }, [snackbarHandler]);

  const brandChangeHandler = (brand) => {
    let brandId = "";
    if (brand.length > 0) {
      brandId = brand[0].id;
      setReloadModel(true);
    }
    setSelectedBrand(brand);
    setToFilter((prev) => ({ ...prev, brandId }));
  };

  const modelChangeHandler = (model) => {
    let modelId = "";
    if (model.length > 0) {
      modelId = model[0].id;
    }
    setSelectedModel(model);
    setToFilter((prev) => ({ ...prev, modelId }));
  };

  const clientChangeHandler = (client) => {
    let clientId = "";
    if (client.length > 0) {
      clientId = client[0].id;
    }
    setSelectedClient(client);
    setToFilter((prev) => ({ ...prev, clientId }));
  };

  const stateChangeHandler = (state) => {
    let stateId = "";
    if (state.length > 0) {
      stateId = state[0].id;
    }
    setSelectedState(state);
    setToFilter((prev) => ({ ...prev, stateId }));
  };

  const serialNumberOnchangeHandler = useCallback((event) => {
    let serialNumber = event.target.value;
    setToFilter((prev) => ({ ...prev, serialNumber }));
  }, []);

  const observationOnchangeHandler = useCallback((event) => {
    let observations = event.target.value;
    setToFilter((prev) => ({ ...prev, observations }));
  }, []);

  const filterClickHandler = () => {
    Filter(toFilter);
  };

  return (
    <FilterByStyled className="outline md">
      {loadingBrand && (
        <LoadingBox height={40} width="100%" styles={{ marginBottom: 20 }} />
      )}

      {brandList.length > 0 && !loadingBrand && (
        <InputGroup
          type="autocomplete"
          placeholder={getTranslation("0103")}
          inputElement={{
            id: "brandList",
            list: brandList,
            change: brandChangeHandler,
            value: selectedBrand,
            className: "focused",
            placeholder: getTranslation("0036"),
            inputPlaceholder: `${getTranslation("0036")}/${getTranslation(
              "0052"
            )}`,
            emptyText: ReplaceWord(
              getTranslation("0048"),
              "**LIST**",
              getTranslation("0036").toLowerCase()
            ),
            multiple: false,
            clear: true,
            secondaryOption: "companyName",
          }}
        />
      )}

      {selectedBrand.length > 0 && loadingModel && (
        <LoadingBox height={40} width="100%" styles={{ marginBottom: 20 }} />
      )}

      {selectedBrand.length > 0 && modelList.length > 0 && !loadingModel && (
        <InputGroup
          type="autocomplete"
          placeholder={getTranslation("0104")}
          inputElement={{
            id: "modelList",
            list: modelList,
            change: modelChangeHandler,
            value: selectedModel,
            className: "focused",
            placeholder: getTranslation("0085"),
            inputPlaceholder: getTranslation("0124"),
            emptyText: ReplaceWord(
              getTranslation("0048"),
              "**LIST**",
              getTranslation("0085").toLowerCase()
            ),
            multiple: false,
            clear: true,
          }}
        />
      )}

      {loadingClient && (
        <LoadingBox height={40} width="100%" styles={{ marginBottom: 20 }} />
      )}

      {clientList.length > 0 && !loadingClient && (
        <InputGroup
          type="autocomplete"
          placeholder={getTranslation("0105")}
          inputElement={{
            id: "clientList",
            list: clientList,
            change: clientChangeHandler,
            value: selectedClient,
            className: "focused",
            placeholder: getTranslation("0072"),
            inputPlaceholder: `${getTranslation("0125")}/${getTranslation(
              "0052"
            )}`,
            emptyText: ReplaceWord(
              getTranslation("0048"),
              "**LIST**",
              getTranslation("0072").toLowerCase()
            ),
            multiple: false,
            clear: true,
            secondaryOption: "companyName",
          }}
        />
      )}

      {loadingState && (
        <LoadingBox height={40} width="100%" styles={{ marginBottom: 20 }} />
      )}

      {stateList.length > 0 && !loadingClient && (
        <InputGroup
          type="autocomplete"
          placeholder={getTranslation("0106")}
          inputElement={{
            id: "stateList",
            list: stateList,
            change: stateChangeHandler,
            value: selectedState,
            className: "focused",
            placeholder: getTranslation("0106"),
            inputPlaceholder: getTranslation("0050"),
            emptyText: ReplaceWord(
              getTranslation("0048"),
              "**LIST**",
              getTranslation("0106").toLowerCase()
            ),
            multiple: false,
            clear: true,
            iconText: true,
          }}
        />
      )}

      <InputGroup
        type="input"
        placeholder={getTranslation("0102")}
        inputElement={{
          name: "SerialNumber",
          onChange: serialNumberOnchangeHandler,
          type: "text",
          defaultValue: toFilter.serialNumber,
          className: toFilter.serialNumber ? "focused" : "",
          translate: "0102",
        }}
      />

      <InputGroup
        type="input"
        placeholder={getTranslation("0016")}
        inputElement={{
          name: "Observations",
          onChange: observationOnchangeHandler,
          type: "text",
          defaultValue: toFilter.observations,
          className: toFilter.observations ? "focused" : "",
          translate: "0016",
        }}
      />

      <Button
        theme="default"
        type="button"
        clicked={filterClickHandler}
        icon={<FilterListOutlinedIcon />}
        className="btn-secondary"
        text={getTranslation("0076")}
        style={{ marginTop: "auto" }}
      />
    </FilterByStyled>
  );
};
